import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { ENSURE_NETWORK_TARIFFS, INITIAL_AUTHENTICATION, LOAD_NETWORK_TARIFFS } from '../../actions/actionTypes';
import { Action, PromiseAction } from '../../actions/commonActions';
import {
  loadNetworkTariffsFailure,
  loadNetworkTariffsPending,
  loadNetworkTariffsSuccess,
} from '../../actions/tariffActions';
import * as tariffApi from '../../api/tariffApi';
import { TariffFilter } from '../../api/tariffApi';
import { DropdownItem, TariffDropdownItem } from '../../components/ui/input/DropdownInput';
import { DropdownItemsState, getAccountNetworkKey } from '../../models/state/simProvision/simProvisionState';
import { selectTariffs } from '../../selectors/simProvision';

const log = getLogger('networkTariffs.sagas');

/*
 * A saga which fetches the list of network tariffs
 */
export function* loadNetworkTariffsSaga(action: PromiseAction<TariffFilter, DropdownItem[]>) {
  const { payload } = action;
  if (!payload) {
    return;
  }

  yield put(loadNetworkTariffsPending(payload));
  try {
    const tariffs: TariffDropdownItem[] = yield call(tariffApi.getTariffs, payload);
    yield put(loadNetworkTariffsSuccess(payload, tariffs));
    action.resolve && action.resolve(tariffs);
  } catch (error: any) {
    yield put(loadNetworkTariffsFailure(payload, error));
    log.error(error);
  }
}

/*
 * A saga which fetches the network tariffs if needed
 */
export function* ensureNetworkTariffsSaga(action: Action<TariffFilter>) {
  const filter = action.payload;
  if (!filter) {
    return;
  }

  const tariffsState: DropdownItemsState = yield select(selectTariffs);
  const accountNetworkKey = getAccountNetworkKey(filter.accountId, filter.networkId);
  const tariff = tariffsState[accountNetworkKey];
  if (tariff) {
    const { expiresOn, isLoading } = tariff;
    if (isLoading || (expiresOn && expiresOn > new Date())) {
      return;
    }
  }

  yield call(loadNetworkTariffsSaga, action);
}

/*
 * Initializes all sagas that are related to network tariffs
 */
export function* initNetworkTariffSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_NETWORK_TARIFFS, ensureNetworkTariffsSaga);
  yield takeEvery(LOAD_NETWORK_TARIFFS, loadNetworkTariffsSaga);
}
